import React from 'react';

interface Position {
  symbol: string;
  quantity: number;
  entryPrice: number;
  currentPrice: number;
  marketValue: number;
  unrealizedPnL: number;
  unrealizedPnLPercent: number;
}

interface AccountInfo {
  equity: number;
  cash: number;
  buyingPower: number;
  portfolioValue: number;
}

interface PortfolioOverviewProps {
  account: AccountInfo;
  positions: Position[];
}

const PortfolioOverview: React.FC<PortfolioOverviewProps> = ({ account, positions }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      {/* Account Summary */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <h2 style={{
          margin: '0 0 16px 0',
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#1e293b'
        }}>
          Portfolio Overview
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              Total Equity
            </div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>
              {formatCurrency(account.equity)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              Cash Available
            </div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>
              {formatCurrency(account.cash)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
              Buying Power
            </div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>
              {formatCurrency(account.buyingPower)}
            </div>
          </div>
        </div>
      </div>

      {/* Positions Table */}
      <div style={{ padding: '20px 24px' }}>
        <h3 style={{
          margin: '0 0 16px 0',
          fontSize: '16px',
          fontWeight: '600',
          color: '#1e293b'
        }}>
          Current Positions
        </h3>

        {positions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            No open positions
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{
                  borderBottom: '2px solid #e5e7eb',
                  textAlign: 'left'
                }}>
                  <th style={{ padding: '12px 8px', fontWeight: '600', color: '#6b7280' }}>Symbol</th>
                  <th style={{ padding: '12px 8px', fontWeight: '600', color: '#6b7280' }}>Quantity</th>
                  <th style={{ padding: '12px 8px', fontWeight: '600', color: '#6b7280' }}>Entry Price</th>
                  <th style={{ padding: '12px 8px', fontWeight: '600', color: '#6b7280' }}>Current Price</th>
                  <th style={{ padding: '12px 8px', fontWeight: '600', color: '#6b7280' }}>Market Value</th>
                  <th style={{ padding: '12px 8px', fontWeight: '600', color: '#6b7280' }}>Unrealized P&L</th>
                  <th style={{ padding: '12px 8px', fontWeight: '600', color: '#6b7280' }}>Return %</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((position) => (
                  <tr
                    key={position.symbol}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <td style={{ padding: '12px 8px' }}>
                      <span style={{
                        fontWeight: '600',
                        color: '#1e293b'
                      }}>
                        {position.symbol}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px', color: '#4b5563' }}>
                      {position.quantity}
                    </td>
                    <td style={{ padding: '12px 8px', color: '#4b5563' }}>
                      {formatCurrency(position.entryPrice)}
                    </td>
                    <td style={{ padding: '12px 8px', color: '#4b5563' }}>
                      {formatCurrency(position.currentPrice)}
                    </td>
                    <td style={{ padding: '12px 8px', color: '#4b5563' }}>
                      {formatCurrency(position.marketValue)}
                    </td>
                    <td style={{
                      padding: '12px 8px',
                      color: position.unrealizedPnL >= 0 ? '#10b981' : '#ef4444',
                      fontWeight: '600'
                    }}>
                      {formatCurrency(position.unrealizedPnL)}
                    </td>
                    <td style={{
                      padding: '12px 8px',
                      color: position.unrealizedPnLPercent >= 0 ? '#10b981' : '#ef4444',
                      fontWeight: '600'
                    }}>
                      {formatPercent(position.unrealizedPnLPercent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioOverview;
